import React from 'react'
import { HiShieldCheck } from 'react-icons/hi2'
import { MarketAirconDeals } from '../index/service-category/aircon-service/AirconDealItem'
import { AirconGeneralServiceProcess } from '../index/service-category/aircon-service/AirconGeneralServiceProcess'
import { AirconChemicalWashProcess } from '../index/service-category/aircon-service/AirconChemicalWashProcess'
import { AirconChemicalOverhaulProcess } from '../index/service-category/aircon-service/AirconChemicalOverhaulProcess'
import { AirconCustomerReviews } from '../index/service-category/aircon-service/AirconCustomerReviews'
import { AirconPriceComparison } from '../index/service-category/aircon-service/AirconPriceComparison'
import { buildMarketDeals } from '../../utils/market-aircon-booking'
import { hasFeature, t } from '../../utils/markets'
import { currencyLabel, formatAmount, formatMoney } from '../../utils/format-money'
import { useMarketBooking } from './MarketBooking'

// UAE / Malaysia wrappers around the Singapore aircon page sections. Each one
// feeds a shared SG component its market copy (components/market/sections.js)
// and, where it books, the MarketBooking handlers - so the sections look and
// behave as on the Singapore pages. Render inside <MarketBooking>.

// "Deals" block, as in Aircon.js: repair problems and one row per service.
export const MarketDealsSection = ({ title, tables }) => {
    const { market, book, showJobScope, openItemSelection } = useMarketBooking()
    const money = (v) => formatMoney(v, market.countryCd)
    return (
        <section className="relative py-12 md:py-16 overflow-hidden">
            <div className="max-w-5xl mx-auto px-4">
                <div className="text-center mb-8">
                    <h2 className="text-2xl md:text-3xl font-extrabold text-gray-900 tracking-tight">{title}</h2>
                </div>
                <div className="md:space-y-5 pt-2 px-1">
                    <MarketAirconDeals
                        deals={buildMarketDeals(market, tables)}
                        book={book}
                        showJobScope={(key) => showJobScope(key, t(market, 'mountWall'))}
                        openMore={openItemSelection}
                        money={money}
                        amount={(v) => formatAmount(v, market.countryCd)}
                        currency={currencyLabel(market.countryCd)}
                    />
                </div>
            </div>
        </section>
    )
}

// Price comparison table for wall units, from the market's price tables.
export const MarketPriceComparison = ({ copy, tables, priceHref }) => {
    const { market } = useMarketBooking()
    return (
        <AirconPriceComparison
            copy={copy}
            tiers={tables.general.map((row) => ({ units: row.units, total: row.wall }))}
            money={(v) => formatMoney(v, market.countryCd)}
            priceHref={priceHref}
        />
    )
}

const PROCESS = {
    general: AirconGeneralServiceProcess,
    chemicalWash: AirconChemicalWashProcess,
    chemicalOverhaul: AirconChemicalOverhaulProcess,
}

// Step-by-step process for one service (general / chemicalWash /
// chemicalOverhaul), with the market's own step copy.
export const MarketProcessSection = ({ service, copy }) => {
    const { market } = useMarketBooking()
    const Process = PROCESS[service]
    if (!Process || !copy) return null
    return (
        <section className="max-w-5xl mx-auto py-5 md:py-10">
            <Process market={market} steps={copy.steps} title={copy.title} intro={copy.intro} />
        </section>
    )
}

// Reviews, behind the market's `reviews` switch (utils/markets.js).
export const MarketReviewsSection = ({ testimonials }) => {
    const { market } = useMarketBooking()
    if (!hasFeature(market, 'reviews') || !testimonials?.length) return null
    return (
        <AirconCustomerReviews
            testimonials={testimonials}
            title={`What Customers Say About Our ${t(market, 'acCategory')}`}
            intro={`Reviews from customers who booked ${t(market, 'acShort')} servicing through StringsSG`}
            showRating={hasFeature(market, 'googleRating')}
        />
    )
}

// The boxed "regulations" note at the end of the Singapore hub, with the
// market's own copy.
export const LocalRulesSection = ({ copy }) => (
    <section className="py-6 md:py-10">
        <div className="max-w-4xl mx-auto px-4">
            <div className="bg-white rounded-2xl border border-gray-100 p-5 md:p-6">
                <div className="flex items-center gap-2.5 mb-3">
                    <HiShieldCheck className="text-orange-500 text-xl shrink-0" />
                    <h2 className="text-lg md:text-xl font-extrabold text-gray-900 tracking-tight">{copy.title}</h2>
                </div>
                <div className="space-y-2.5 text-sm text-gray-600 leading-relaxed">
                    {copy.paragraphs.map((p) => <p key={p}>{p}</p>)}
                </div>
            </div>
        </div>
    </section>
)
